import React, { useMemo, useState } from "react";
import { FaPlus, FaTimes } from "react-icons/fa";
import { useNotify } from "../../components/ConfirmModal";
import { AreaChip, Modal, PersonSelect } from "./WorkUi";
import { errorMessage } from "./workApi";

const blankRow = (type, executor) => ({
  Title: "",
  Item_Type: type || "Task",
  Executor_Id: executor || null,
  Due_Date: "",
  Planned_Post_Date: "",
  Platform: "",
});

/**
 * Assign work in three steps: 1) which area (Video, Media, Campaign...), 2) who leads, reviews and
 * does it, 3) what to deliver and by when. Everyone gets one email with their list.
 */
function AssignWizard({ api, projectType, projectId, meta, plan, initialAreaId, onClose, onDone }) {
  const notify = useNotify();
  const lanes = useMemo(() => Object.fromEntries((plan?.streams || []).map((s) => [s.Area_Id, s])), [plan]);
  const [step, setStep] = useState(initialAreaId ? 2 : 1);
  const [areaId, setAreaId] = useState(initialAreaId || null);
  const area = (meta?.areas || []).find((a) => a.Area_Id === areaId);
  const lane = lanes[areaId];
  const [people, setPeople] = useState(() => ({
    Lead_Id: lane?.Lead_Id || null,
    Reviewer_Id: lane?.Reviewer_Id || null,
    Executor_Id: null,
    Due_Date: lane?.Due_Date || "",
  }));
  const [rows, setRows] = useState([blankRow(area?.Default_Item_Type)]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const pickArea = (a) => {
    setAreaId(a.Area_Id);
    const existing = lanes[a.Area_Id];
    setPeople((p) => ({
      ...p,
      Lead_Id: existing?.Lead_Id ?? p.Lead_Id,
      Reviewer_Id: existing?.Reviewer_Id ?? p.Reviewer_Id,
      Due_Date: existing?.Due_Date || p.Due_Date,
    }));
    setRows((r) => r.map((row) => (row.Title ? row : { ...row, Item_Type: a.Default_Item_Type || "Task" })));
  };

  const setRow = (i, key, value) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: value } : row)));

  const next = () => {
    setError("");
    if (step === 1 && !areaId) return setError("Pick an area to continue.");
    if (step === 2 && !people.Executor_Id && !people.Lead_Id) return setError("Pick who leads this or who does the work.");
    if (step === 2) setRows((r) => r.map((row) => ({ ...row, Executor_Id: row.Executor_Id || people.Executor_Id || people.Lead_Id })));
    setStep((s) => s + 1);
    return undefined;
  };

  const submit = async () => {
    const items = rows.filter((r) => r.Title.trim());
    if (!items.length && !lane) {
      setError("Add at least one thing to deliver, or go back and just set the lead.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const body = {
        Area_Id: areaId,
        Lead_Id: people.Lead_Id,
        Reviewer_Id: people.Reviewer_Id,
        Due_Date: people.Due_Date || null,
        Executor_Id: people.Executor_Id,
        items: items.map((r) => ({
          ...r,
          Title: r.Title.trim(),
          Due_Date: r.Due_Date || null,
          Planned_Post_Date: r.Planned_Post_Date || null,
          Platform: r.Platform || null,
        })),
      };
      const res = await api.assign(projectType, projectId, body);
      notify.success(items.length ? `Assigned ${items.length} item(s). Emails are on the way.` : "Lane saved");
      onDone?.(res);
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not assign"));
    } finally {
      setSaving(false);
    }
  };

  const steps = ["Area", "People", "Deliverables & dates"];

  return (
    <Modal
      title="Assign work"
      onClose={onClose}
      wide
      footer={
        <>
          {step > 1 ? (
            <button type="button" className="dmp-btn dmp-btn--ghost wk-btn-lg" onClick={() => setStep((s) => s - 1)}>
              Back
            </button>
          ) : null}
          {step < 3 ? (
            <button type="button" className="dmp-btn wk-btn-lg" onClick={next}>
              Next
            </button>
          ) : (
            <button type="button" className="dmp-btn wk-btn-lg" onClick={submit} disabled={saving}>
              {saving ? "Assigning…" : "Assign and notify"}
            </button>
          )}
        </>
      }
    >
      <ol className="wk-steps" aria-label="Steps">
        {steps.map((label, i) => (
          <li key={label} className={step === i + 1 ? "is-active" : step > i + 1 ? "is-done" : ""} aria-current={step === i + 1 ? "step" : undefined}>
            <span>{i + 1}</span>
            {label}
          </li>
        ))}
      </ol>
      {error ? <div className="dmp-error">{error}</div> : null}

      {step === 1 ? (
        <>
          <p className="wk-muted">What kind of work is this?</p>
          <div className="wk-area-pick" role="radiogroup" aria-label="Area">
            {(meta?.areas || []).map((a) => (
              <button
                key={a.Area_Id}
                type="button"
                role="radio"
                aria-checked={areaId === a.Area_Id}
                className={areaId === a.Area_Id ? "is-selected" : ""}
                style={{ "--wk-area": a.Color }}
                onClick={() => pickArea(a)}
              >
                <span className="wk-area-pick__name">
                  <AreaChip name={a.Area_Name} color={a.Color} icon={a.Icon} />
                </span>
                <span className="wk-area-pick__desc">
                  {a.Description}
                  {lanes[a.Area_Id] ? <strong> · Lane exists ({lanes[a.Area_Id].Lead_Name || "no lead"})</strong> : null}
                </span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {step === 2 ? (
        <>
          <p className="wk-muted">
            {area ? <AreaChip name={area.Area_Name} color={area.Color} icon={area.Icon} /> : null} Who is responsible?
          </p>
          <div className="wk-row">
            <label className="wk-field">
              Lead (plans and owns this area)
              <PersonSelect people={meta?.people} value={people.Lead_Id} onChange={(v) => setPeople((p) => ({ ...p, Lead_Id: v }))} />
            </label>
            <label className="wk-field">
              Reviewer (approves the work)
              <PersonSelect
                people={meta?.people}
                value={people.Reviewer_Id}
                onChange={(v) => setPeople((p) => ({ ...p, Reviewer_Id: v }))}
                blank="Project owner reviews"
              />
            </label>
          </div>
          <div className="wk-row">
            <label className="wk-field">
              Who does the work (default)
              <PersonSelect people={meta?.people} value={people.Executor_Id} onChange={(v) => setPeople((p) => ({ ...p, Executor_Id: v }))} blank="Same as lead" />
            </label>
            <label className="wk-field">
              Area deadline (optional)
              <input type="date" value={people.Due_Date || ""} onChange={(e) => setPeople((p) => ({ ...p, Due_Date: e.target.value }))} />
            </label>
          </div>
        </>
      ) : null}

      {step === 3 ? (
        <>
          <p className="wk-muted">List what needs to be delivered. Add a post date to put it on the posting calendar.</p>
          <div className="wk-item-rows">
            {rows.map((row, i) => (
              <div className="wk-item-row" key={i}>
                {rows.length > 1 ? (
                  <button
                    type="button"
                    className="wk-close wk-item-row__remove"
                    aria-label={`Remove item ${i + 1}`}
                    onClick={() => setRows((r) => r.filter((_, idx) => idx !== i))}
                  >
                    <FaTimes aria-hidden="true" />
                  </button>
                ) : null}
                <div className="wk-row">
                  <label className="wk-field" style={{ gridColumn: "span 2" }}>
                    What to deliver
                    <input value={row.Title} onChange={(e) => setRow(i, "Title", e.target.value)} placeholder="e.g. Diwali offer reel" maxLength={200} />
                  </label>
                  <label className="wk-field">
                    Type
                    <select value={row.Item_Type} onChange={(e) => setRow(i, "Item_Type", e.target.value)}>
                      {(meta?.item_types || []).map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="wk-row">
                  <label className="wk-field">
                    Doing it
                    <PersonSelect people={meta?.people} value={row.Executor_Id} onChange={(v) => setRow(i, "Executor_Id", v)} />
                  </label>
                  <label className="wk-field">
                    Due
                    <input type="date" value={row.Due_Date} onChange={(e) => setRow(i, "Due_Date", e.target.value)} />
                  </label>
                  <label className="wk-field">
                    Post on
                    <input type="date" value={row.Planned_Post_Date} onChange={(e) => setRow(i, "Planned_Post_Date", e.target.value)} />
                  </label>
                  <label className="wk-field">
                    Platform
                    <select value={row.Platform} onChange={(e) => setRow(i, "Platform", e.target.value)}>
                      <option value="">—</option>
                      {(meta?.platforms || []).map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="dmp-btn dmp-btn--ghost"
            style={{ marginTop: 10 }}
            onClick={() => setRows((r) => [...r, blankRow(area?.Default_Item_Type, people.Executor_Id || people.Lead_Id)])}
          >
            <FaPlus aria-hidden="true" /> Add another
          </button>
        </>
      ) : null}
    </Modal>
  );
}

export default AssignWizard;
